import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { exchangeCodeForToken } from '../services/auth';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  background-color: #121212;
  color: white;
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  border-top: 4px solid white;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Message = styled.h2`
  font-size: 24px;
  margin-bottom: 16px;
  text-align: center;
`;

const ErrorMessage = styled.p`
  color: #ff6b6b;
  margin-top: 16px;
  text-align: center;
`;

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');
  
  useEffect(() => {
    const processAuth = async () => {
      try {
        // Parse URL parameters
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const state = params.get('state');
        const error = params.get('error');
        
        // Check for errors from Google
        if (error) {
          console.error('Error from Google:', error);
          setStatus('error');
          setError(`Authentication error: ${error}`);
          return;
        }
        
        // Verify we have the required parameters
        if (!code) {
          console.error('No authorization code received');
          setStatus('error');
          setError('No authorization code received');
          return;
        }
        
        // Verify the state parameter to prevent CSRF attacks
        const storedState = localStorage.getItem('auth_state');
        const stateTimestamp = localStorage.getItem('auth_state_timestamp');
        
        // Clear stored state immediately after retrieving it
        localStorage.removeItem('auth_state');
        localStorage.removeItem('auth_state_timestamp');
        
        // Validate state parameter
        if (!state || state !== storedState) {
          console.error('Invalid state parameter');
          setStatus('error');
          setError('Security validation failed. Please try again.');
          return;
        }
        
        // Check if state is expired (15 minutes)
        const fifteenMinutes = 15 * 60 * 1000;
        if (stateTimestamp && Date.now() - parseInt(stateTimestamp) > fifteenMinutes) {
          console.error('State parameter expired');
          setStatus('error');
          setError('Authentication request expired. Please try again.');
          return;
        }
        
        // Exchange code for token
        console.log('Exchanging authorization code for token');
        const result = await exchangeCodeForToken(code);
        
        if (result.isAuthenticated) {
          console.log('Authentication successful, redirecting to dashboard');
          setStatus('success');
          // Redirect to dashboard after a brief delay to show success message
          setTimeout(() => navigate('/dashboard'), 1000);
        } else {
          console.error('Token exchange failed:', result.error);
          setStatus('error');
          setError(result.error || 'Failed to complete authentication');
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        setStatus('error');
        setError(error.message || 'An unexpected error occurred');
      }
    };
    
    processAuth();
  }, [navigate, location.search]);
  
  return (
    <Container>
      {status === 'processing' && (
        <>
          <LoadingSpinner />
          <Message>Completing your sign in...</Message>
        </>
      )}
      
      {status === 'success' && (
        <Message>Sign in successful! Redirecting...</Message>
      )}
      
      {status === 'error' && (
        <>
          <Message>Sign in failed</Message>
          <ErrorMessage>{error}</ErrorMessage>
          <button onClick={() => navigate('/')}>Return to Home</button>
        </>
      )}
    </Container>
  );
};

export default AuthCallback;
